<template>
  <DialogRoot :open="true" :modal="true">
    <DialogPortal>
      <DialogOverlay class="modal-backdrop" />
      <DialogContent
        v-bind="$attrs"
        class="modal-dialog"
        :style="cardStyles"
        :aria-describedby="undefined"
        @escape-key-down="onEscapeKey"
        @pointer-down-outside="onOutsideClick"
        @interact-outside="onOutsideClick"
      >
        <header class="modal-card-head">
          <DialogTitle as-child>
            <p class="modal-card-title p-0">
              <slot name="title" class-name="modal-card-title">
                <!-- eslint-disable-next-line vue/no-v-html -->
                <span v-html="sanitizedTitle" />
              </slot>
            </p>
          </DialogTitle>
          <slot name="header-extra" />
          <button class="delete ml-auto" @click="handleClose" />
        </header>
        <div v-if="loading" class="modal-progress">
          <div class="modal-progress__bar" />
        </div>
        <section class="modal-card-body">
          <slot name="body">
            <slot />
          </slot>
        </section>
        <footer v-if="$slots['footer'] && !loading" class="modal-card-foot">
          <slot name="footer" />
        </footer>
      </DialogContent>
    </DialogPortal>
  </DialogRoot>
</template>

<script>
import {
  DialogContent,
  DialogOverlay,
  DialogPortal,
  DialogRoot,
  DialogTitle,
} from 'reka-ui'
import { computed } from 'vue'

import { sanitizeHtml } from '@/utils/sanitize'

export default {
  name: 'ModalCard',
  inheritAttrs: false,
  components: {
    DialogRoot,
    DialogPortal,
    DialogOverlay,
    DialogContent,
    DialogTitle,
  },
  props: {
    title: { type: String },
    loading: { type: Boolean, default: false },
    handleClose: { type: Function },
    width: { type: String },
    height: { type: String },
    // Set false for modals holding unsaved work, where a stray click on the
    // backdrop would throw it away. Escape and the close button still work.
    closeOnOutsideClick: { type: Boolean, default: true },
  },
  setup(props) {
    const sanitizedTitle = computed(() => sanitizeHtml(props.title ?? ''))

    const cardStyles = computed(() => {
      const styles = {}
      if (props.width) {
        styles.width = props.width
        styles.maxWidth = props.width
      }
      if (props.height) styles.height = props.height
      return styles
    })

    const hasOpenedDropdowns = () => {
      const activeDropdownSelect = '.multisel-wrapper:has(.multiselect--active)'
      return document.querySelector(activeDropdownSelect) !== null
    }

    const close = () => props.handleClose?.()

    const onEscapeKey = (e) => {
      if (hasOpenedDropdowns()) {
        e.preventDefault()
        return
      }
      close()
    }

    const onOutsideClick = (e) => {
      if (!props.closeOnOutsideClick) {
        e.preventDefault()
        return
      }
      if (hasOpenedDropdowns()) {
        e.preventDefault()
        return
      }
      const target = e.detail?.originalEvent?.target
      if (target?.closest('[data-outside-click-ignore]')) {
        e.preventDefault()
        return
      }
      close()
    }

    return { cardStyles, sanitizedTitle, onEscapeKey, onOutsideClick }
  },
}
</script>

<style lang="scss" scoped>
// In-flow progress bar: sits naturally between header and body in the flex
// column, so it appears below the header (not overlapping it like position:absolute).
.modal-progress {
  height: 3px;
  background-color: var(--border-default);
  overflow: hidden;
  flex-shrink: 0;
}

.modal-progress__bar {
  height: 100%;
  width: 40%;
  background-color: var(--primary);
  border-radius: 2px;
  animation: modal-progress-slide 1.2s ease-in-out infinite;
}

@keyframes modal-progress-slide {
  0% {
    transform: translateX(-100%);
  }
  100% {
    transform: translateX(350%);
  }
}
</style>

<!-- Portal styles in ui-elements.scss -->

<style lang="scss">
// Removes modal-card-body padding + caps dialog height at 85vh so the modal
// never overflows the screen. Children handle their own scrolling.
// Usage: <ModalCard class="edge-to-edge">
.edge-to-edge.modal-dialog {
  max-height: 85vh;
}

// Both classes on the same element → specificity (0,3,0) beats
// .modal-dialog .modal-card-body (0,2,0) defined in ui-elements.scss
.edge-to-edge.modal-dialog .modal-card-body {
  padding: 0;
}
</style>
